import React, { forwardRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FiX } from 'react-icons/fi';
import classNames from 'classnames';

const ConfirmationModal = forwardRef(({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'default' // Can be 'default', 'danger', 'warning'
}, ref) => {
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }
    return () => {
      // Restore scroll position when modal closes
      if (document.body.style.position === 'fixed') {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Define button styles based on type
  const getButtonStyles = () => {
    const baseStyle = 'px-4 py-2 rounded-md text-sm font-medium transition-all duration-300';
    const styles = {
      default: 'bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110',
      danger: 'bg-accent-secondary-light dark:bg-accent-secondary-dark text-primary-light dark:text-primary-dark hover:brightness-110',
      warning: 'bg-accent-tertiary-light dark:bg-accent-tertiary-dark text-primary-light dark:text-primary-dark hover:brightness-110'
    };
    return `${baseStyle} ${styles[type]}`;
  };

  // Create the modal content
  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      
      {/* Modal Content */}
      <div 
        ref={ref}
        className={classNames(
          'relative z-10',
          'w-full max-w-md mx-auto my-8',
          'bg-primary-light dark:bg-primary-dark',
          'text-text-light dark:text-text-dark',
          'p-5 sm:p-7',
          'rounded-lg shadow-2xl',
          'border-2 border-secondary-light dark:border-secondary-dark',
          'transform transition-all duration-300 ease-out'
        )}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-text-light/70 dark:text-text-dark/70 hover:text-text-light dark:hover:text-text-dark transition-colors duration-300"
          aria-label="Close modal"
        >
          <FiX className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="pr-8">
          <h3 
            className="text-xl font-bold mb-3"
            id="modal-title"
          >
            {title}
          </h3>
          <div className="mt-2">
            <p className="text-base text-text-light/90 dark:text-text-dark/90">
              {message}
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-8 flex flex-row-reverse gap-4">
          <button
            type="button"
            className={`${getButtonStyles()} px-6 py-2.5 font-semibold text-base`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
          <button
            type="button"
            className="px-6 py-2.5 rounded-md text-base font-medium bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark hover:bg-secondary-light/80 dark:hover:bg-secondary-dark/80 transition-all duration-300"
            onClick={onClose}
          >
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render the modal outside of the component hierarchy
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
});

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  type: PropTypes.oneOf(['default', 'danger', 'warning'])
};

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal; 